import { AnimationOutTypes } from '../../Enums/AnimationOutTypes';
import { Directions } from '../../Enums/Directions';
import { RegionTypes } from '../../Enums/RegionTypes';
import KeyFrameHelper, { KeyFrame } from '../../Helpers/KeyFrames';
import { createCirclePath, createRectanglePath } from '../../Helpers/pathFunctions';
import { Position } from '../Shared/Position';
import { BaseAnimationOut } from './BaseAnimation';
import { BaseVisualElement } from '../Elements/BaseVisualElement';
import { calculateWipe } from './wipeCalculation';
import { type AnimationChanges, type AnimationTarget, mergeAnimationChanges } from './AnimationChanges';
import {
    type AnimationOutConfig,
    type BlurOutAnimationConfig,
    type FadeBlurOutAnimationConfig,
    type FadeMoveOutAnimationConfig,
    type FadeOutAnimationConfig,
    type FadeScaleOutAnimationConfig,
    type IrisOutAnimationConfig,
    type MoveOutAnimationConfig,
    type PopOutAnimationConfig,
    type ScaleOutAnimationConfig,
    type WipeOutAnimationConfig,
} from './types';

export type AnimationOut =
    | FadeOutAnimation
    | MoveOutAnimation
    | ScaleOutAnimation
    | BlurOutAnimation
    | IrisOutAnimation
    | WipeOutAnimation
    | PopOutAnimation
    | FadeMoveOutAnimation
    | FadeScaleOutAnimation
    | FadeBlurOutAnimation;

class FadeOutAnimation extends BaseAnimationOut<FadeOutAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const startOpacity = target.opacity;
        const finalOpacity = this.config.opacity || 0;
        const newOpacity = this.timingFn(
            frameIndex - this.startFrame,
            startOpacity,
            finalOpacity - startOpacity,
            this.duration - 1,
        );

        return { opacity: newOpacity };
    }
}

class MoveOutAnimation extends BaseAnimationOut<MoveOutAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const direction = this.config.direction;
        const distance = this.config.distance;
        const mask = this.config.mask;
        const startPosition = target.getAbsContentPosition();
        const currentDimension = target.contentBox.dimension;
        const canvasDimension = this.el.canvasDimension;
        const finalPosition = new Position(startPosition.getX(), startPosition.getY());

        switch (direction) {
            case Directions.DOWN: {
                finalPosition.setY(
                    startPosition.getY() + (canvasDimension.getHeight() - startPosition.getY()) * distance,
                );
                break;
            }

            case Directions.UP: {
                finalPosition.setY(
                    startPosition.getY() - (currentDimension.getHeight() + startPosition.getY()) * distance,
                );
                break;
            }

            case Directions.LEFT: {
                finalPosition.setX(
                    startPosition.getX() - (currentDimension.getWidth() + startPosition.getX()) * distance,
                );
                break;
            }

            case Directions.RIGHT: {
                finalPosition.setX(
                    startPosition.getX() + (canvasDimension.getWidth() - startPosition.getX()) * distance,
                );
                break;
            }
        }

        const newX = this.timingFn(
            frameIndex - this.startFrame,
            startPosition.getX(),
            finalPosition.getX() - startPosition.getX(),
            this.duration - 1,
        );
        const newY = this.timingFn(
            frameIndex - this.startFrame,
            startPosition.getY(),
            finalPosition.getY() - startPosition.getY(),
            this.duration - 1,
        );

        const translateX = startPosition.getX() - newX;
        const translateY = startPosition.getY() - newY;
        const changes: AnimationChanges = {};

        if (mask && mask !== 'false') {
            const contentOffsetX = target.contentBox.position.getX();
            const contentOffsetY = target.contentBox.position.getY();
            const BBWidth = target.boundingBox.dimension.width;
            const BBHeight = target.boundingBox.dimension.height;

            switch (direction) {
                case Directions.DOWN:
                    changes.clipPath = createRectanglePath(
                        0,
                        translateY,
                        BBWidth - contentOffsetX,
                        BBHeight - contentOffsetY,
                    );
                    break;
                case Directions.UP:
                    changes.clipPath = createRectanglePath(
                        0 - contentOffsetX,
                        translateY - contentOffsetY,
                        BBWidth + contentOffsetX,
                        BBHeight + contentOffsetY,
                    );
                    break;
                case Directions.RIGHT:
                    changes.clipPath = createRectanglePath(
                        translateX,
                        0,
                        BBWidth - contentOffsetX,
                        BBHeight - contentOffsetY,
                    );
                    break;
                case Directions.LEFT:
                    changes.clipPath = createRectanglePath(
                        translateX - contentOffsetX,
                        0 - contentOffsetY,
                        BBWidth + contentOffsetX,
                        BBHeight + contentOffsetY,
                    );
                    break;
            }
        }

        // translate to relative box position
        changes.boxPosition = {
            x: target.boundingBox.position.getX() - translateX,
            y: target.boundingBox.position.getY() - translateY,
        };
        changes.translatedX = translateX;
        changes.translatedY = translateY;
        changes.motionBlur = target.motionBlur || (this.config.motionBlur ?? false);

        return changes;
    }
}

class ScaleOutAnimation extends BaseAnimationOut<ScaleOutAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const scaleFrom = target.scale;
        const scaleTo = this.config.scale;
        const newScale = this.timingFn(frameIndex - this.startFrame, scaleFrom, scaleTo - scaleFrom, this.duration - 1);

        // set motion blur if enabled
        return { scale: newScale, motionBlur: target.motionBlur || (this.config.motionBlur ?? false) };
    }
}

class BlurOutAnimation extends BaseAnimationOut<BlurOutAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const blurFrom = target.blur;
        const blurTo = this.config.blur;
        const newBlur = this.timingFn(frameIndex - this.startFrame, blurFrom, blurTo - blurFrom, this.duration - 1);

        return { blur: newBlur };
    }
}

class IrisOutAnimation extends BaseAnimationOut<IrisOutAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        // The transformed region is used to center the effect, while the content is used to determine the radius
        // TODO: this might fail when combined with scaling
        const contentRegion = target.getRegion(RegionTypes.TRANSFORMED);
        const regionWidth = contentRegion.getWidth();
        const regionHeight = contentRegion.getHeight();
        // const contentWidth = compEl.contentBox.dimension.getWidth();
        // const contentHeight = compEl.contentBox.dimension.getHeight();
        const ox = contentRegion.left;
        const oy = contentRegion.top;

        const distance = Math.sqrt((regionWidth / 2) ** 2 + (regionHeight / 2) ** 2);
        const newRadius = this.timingFn(frameIndex - this.startFrame, distance, 0 - distance, this.duration - 1);

        return {
            clipPath: createCirclePath(ox + regionWidth / 2, oy + regionHeight / 2, newRadius),
            // set motion blur if enabled
            motionBlur: target.motionBlur || (this.config.motionBlur ?? false),
        };
    }
}

class WipeOutAnimation extends BaseAnimationOut<WipeOutAnimationConfig> {
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const direction = this.config.direction;
        const softness = this.config.softness;

        const contentRegion = target.getRegion(RegionTypes.TRANSFORMED);
        const w = contentRegion.getWidth();
        const h = contentRegion.getHeight();
        const ox = contentRegion.left;
        const oy = contentRegion.top;

        // Note that we negate this as opposed to WipeIn

        const directionMap = {
            [Directions.UP]: Directions.DOWN,
            [Directions.RIGHT]: Directions.LEFT,
            [Directions.DOWN]: Directions.UP,
            [Directions.LEFT]: Directions.RIGHT,
            [Directions.BOTTOM_LEFT]: Directions.TOP_RIGHT,
            [Directions.BOTTOM_RIGHT]: Directions.TOP_LEFT,
            [Directions.TOP_LEFT]: Directions.BOTTOM_RIGHT,
            [Directions.TOP_RIGHT]: Directions.BOTTOM_LEFT,
        };

        const p = this.timingFn(frameIndex - this.startFrame, 1, -1, this.duration - 1); // get distance percentage
        const params = { ox, oy, w, h, p };
        const { clipPath, clipSoftness } = calculateWipe(directionMap[direction as Directions], softness, params);

        // set motion blur if enabled
        return { clipPath, clipSoftness, motionBlur: target.motionBlur || (this.config.motionBlur ?? false) };
    }
}

class PopOutAnimation extends BaseAnimationOut<PopOutAnimationConfig> {
    // scale to 110% in 20% of time then to 0% in the remaining 80%;
    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }

        const scaleFrom = target.scale;
        const keyFrameHelper = new KeyFrameHelper([
            new KeyFrame(0.0, Number(scaleFrom)), // scale is 1.0 at 0%
            new KeyFrame(0.2, scaleFrom * 1.1), // scale to 1.1 at 20%
            new KeyFrame(1.0, scaleFrom * 0.0), // scale to 0.0 at 100%
        ]);
        // get percentage of the animation finished
        const p = this.timingFn(frameIndex - this.startFrame, 0, 1, this.duration - 1); // get distance percentage

        // apply percentage to relative keyframes
        const newScale = keyFrameHelper.getValueLinear(p);

        // set motion blur if enabled
        return { scale: newScale, motionBlur: target.motionBlur || (this.config.motionBlur ?? false) };
    }
}

class FadeMoveOutAnimation extends BaseAnimationOut<FadeMoveOutAnimationConfig> {
    fadeOutAnimation!: FadeOutAnimation;

    moveOutAnimation!: MoveOutAnimation;

    constructor(config: FadeMoveOutAnimationConfig, el: BaseVisualElement) {
        super(config, el);
        this.fadeOutAnimation = new FadeOutAnimation(config, el);
        this.moveOutAnimation = new MoveOutAnimation(config, el);
    }

    update(config: FadeMoveOutAnimationConfig): void {
        super.update(config);
        this.fadeOutAnimation.update(config);
        this.moveOutAnimation.update(config);
    }

    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }
        const changes = mergeAnimationChanges(
            this.fadeOutAnimation.compute(frameIndex, target),
            this.moveOutAnimation.compute(frameIndex, target),
        );
        // set motion blur if enabled
        changes.motionBlur = (changes.motionBlur ?? target.motionBlur) || (this.config.motionBlur ?? false);

        return changes;
    }
}

class FadeScaleOutAnimation extends BaseAnimationOut<FadeScaleOutAnimationConfig> {
    fadeOutAnimation!: FadeOutAnimation;

    scaleOutAnimation!: ScaleOutAnimation;

    constructor(config: FadeScaleOutAnimationConfig, el: BaseVisualElement) {
        super(config, el);
        this.fadeOutAnimation = new FadeOutAnimation(config, el);
        this.scaleOutAnimation = new ScaleOutAnimation(config, el);
    }

    update(config: FadeScaleOutAnimationConfig): void {
        super.update(config);
        this.fadeOutAnimation.update(config);
        this.scaleOutAnimation.update(config);
    }

    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }
        const changes = mergeAnimationChanges(
            this.fadeOutAnimation.compute(frameIndex, target),
            this.scaleOutAnimation.compute(frameIndex, target),
        );
        // set motion blur if enabled
        changes.motionBlur = (changes.motionBlur ?? target.motionBlur) || (this.config.motionBlur ?? false);

        return changes;
    }
}

class FadeBlurOutAnimation extends BaseAnimationOut<FadeBlurOutAnimationConfig> {
    fadeOutAnimation!: FadeOutAnimation;

    blurOutAnimation!: BlurOutAnimation;

    constructor(config: FadeBlurOutAnimationConfig, el: BaseVisualElement) {
        super(config, el);
        this.fadeOutAnimation = new FadeOutAnimation(config, el);
        this.blurOutAnimation = new BlurOutAnimation(config, el);
    }

    update(config: FadeBlurOutAnimationConfig): void {
        super.update(config);
        this.fadeOutAnimation.update(config);
        this.blurOutAnimation.update(config);
    }

    compute(frameIndex: number, target: AnimationTarget): AnimationChanges | null {
        if (!this.containsFrame(frameIndex) || this.duration <= 1) {
            return null;
        }
        return mergeAnimationChanges(
            this.fadeOutAnimation.compute(frameIndex, target),
            this.blurOutAnimation.compute(frameIndex, target),
        );
    }
}

// ------------------------------------------------------------------------------

export const createAnimationOut = (config: AnimationOutConfig, el: BaseVisualElement): AnimationOut | null => {
    if (!el.animationOut) {
        const elementDuration = el.duration;
        const animationInDuration = el.animationIn?.duration ?? 0;
        const freeDuration = elementDuration - animationInDuration;
        config.duration = freeDuration > 0 ? Math.min(freeDuration, config.duration) : config.duration;
    }

    if (!config) {
        return null;
    }

    switch (config.type) {
        case AnimationOutTypes.FADE_OUT:
            return new FadeOutAnimation(config, el);

        case AnimationOutTypes.MOVE_OUT:
            return new MoveOutAnimation(config, el);

        case AnimationOutTypes.FADE_MOVE_OUT:
            return new FadeMoveOutAnimation(config, el);

        case AnimationOutTypes.SCALE_OUT:
            return new ScaleOutAnimation(config, el);

        case AnimationOutTypes.FADE_SCALE_OUT:
            return new FadeScaleOutAnimation(config, el);

        case AnimationOutTypes.BLUR_OUT:
            return new BlurOutAnimation(config, el);

        case AnimationOutTypes.FADE_BLUR_OUT:
            return new FadeBlurOutAnimation(config, el);

        case AnimationOutTypes.IRIS_OUT:
            return new IrisOutAnimation(config, el);

        case AnimationOutTypes.WIPE_OUT:
            return new WipeOutAnimation(config, el);

        case AnimationOutTypes.POP_OUT:
            return new PopOutAnimation(config, el);
    }
};
